import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';
import { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../lib/utils';

const languages = [
  { code: 'ru', name: 'Русский' },
  { code: 'zh', name: '中文' },
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Español' },
  { code: 'fr', name: 'Français' },
  { code: 'it', name: 'Italiano' },
  { code: 'de', name: 'Deutsch' },
  { code: 'nl', name: 'Dutch' },
  { code: 'pt', name: 'Português' },
  { code: 'pl', name: 'Polski' },
  { code: 'ja', name: '日本語' },
  { code: 'ko', name: '한국어' },
  { code: 'ar', name: 'العربية' },
  { code: 'hi', name: 'हिन्दी' },
];

export default function LanguageSwitcher() {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  // Trigger button ref (for click-outside detection) and for computing
  // the dropdown's screen position when we portal it.
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  // Position of the dropdown in viewport coordinates. Recomputed when
  // the trigger button moves (window resize, scroll).
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    setIsOpen(false);
  };

  // Compute the dropdown's position from the trigger button's rect.
  // useLayoutEffect runs synchronously after the DOM is updated, so the
  // dropdown appears in the right place on the same frame it's opened
  // (no flash).
  useLayoutEffect(() => {
    if (!isOpen) { setPos(null); return; }
    const update = () => {
      const btn = triggerRef.current;
      if (!btn) return;
      const r = btn.getBoundingClientRect();
      // Position the dropdown just under the trigger, right-aligned.
      setPos({
        top: r.bottom + 8,        // 8px gap (mt-2)
        right: window.innerWidth - r.right, // distance from right edge
      });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [isOpen]);

  // Close on click outside EITHER the trigger or the dropdown.
  // The dropdown is portaled to document.body, so we have to check both
  // refs explicitly (the old `dropdownRef.current.contains()` trick no
  // longer works because the dropdown is no longer a DOM child of the
  // trigger wrapper).
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      const t = event.target as Node;
      const inTrigger = triggerRef.current?.contains(t);
      const inDropdown = dropdownRef.current?.contains(t);
      if (!inTrigger && !inDropdown) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Close on Escape (matches the hamburger panel's UX).
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setIsOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen]);

  return (
    <>
      <button
        ref={triggerRef}
        onClick={() => setIsOpen(o => !o)}
        className="p-2 rounded-md bg-muted hover:bg-muted/80 transition-colors flex items-center gap-1"
        title="Change Language"
        aria-label="Change Language"
        aria-expanded={isOpen}
      >
        <Globe className="w-5 h-5" />
        <span className="text-xs uppercase hidden sm:inline">{i18n.language}</span>
      </button>

      {/* Dropdown portaled to document.body so it escapes the header's
          stacking context (the header uses backdrop-blur which creates
          one). Without the portal, the dropdown would be trapped behind
          other content rendered at the page root level. */}
      {isOpen && pos && createPortal(
        <div
          ref={dropdownRef}
          role="listbox"
          style={{
            position: 'fixed',
            top: pos.top,
            right: pos.right,
            zIndex: 100, // matches HamburgerMenu
          }}
          className="w-40 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg overflow-hidden"
        >
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => changeLanguage(lang.code)}
              className={cn(
                "w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors",
                i18n.language === lang.code && "bg-gray-100 dark:bg-gray-700 font-medium"
              )}
            >
              {lang.name}
            </button>
          ))}
        </div>,
        document.body
      )}
    </>
  );
}
